import { useState, useCallback, useEffect } from "react";
import teamService from "../../../services/api/teamService";
import tournamentService from "../../../services/api/tournamentService";
import { buildRosterSummary } from "../../../utils/teamRoster";
import { TEAM_MEMBER_ADMIN_ROLES } from "../../../constants/roles";

/**
 * Custom hook for managing team data fetching and state
 */
export const useTeamData = (teamId, currentUser, isValidTeamId) => {
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [rosterSummary, setRosterSummary] = useState(null);
  const [stats, setStats] = useState([]);
  const [teamStatsSummary, setTeamStatsSummary] = useState(null);
  const [scheduleMatches, setScheduleMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joinRequests, setJoinRequests] = useState([]);
  const [tournamentInvitations, setTournamentInvitations] = useState([]);
  const [userMembership, setUserMembership] = useState(null);
  const [joinRequestStatus, setJoinRequestStatus] = useState(null);

  const fetchTeamData = useCallback(async () => {
    if (!isValidTeamId) return;

    try {
      setLoading(true);
      setError("");

      // Team details and members load together (one round trip, not two)
      const [teamResponse, membersResponse] = await Promise.all([
        teamService.getTeam(teamId),
        teamService.getTeamMembers(teamId),
      ]);
      const teamData = teamResponse?.data?.team || teamResponse?.data || teamResponse;
      let parsedSettings = teamData?.settings;
      if (typeof parsedSettings === "string") {
        try {
          parsedSettings = JSON.parse(parsedSettings);
        } catch (parseError) {
          parsedSettings = {};
        }
      }
      if (typeof parsedSettings === "string") {
        try {
          parsedSettings = JSON.parse(parsedSettings);
        } catch (parseError) {
          parsedSettings = {};
        }
      }

      const normalizedTeam = {
        ...teamData,
        settings: parsedSettings || {},
        requires_approval:
          teamData?.requires_approval ?? parsedSettings?.requires_approval,
        is_public: teamData?.is_public ?? parsedSettings?.is_public,
        max_members: teamData?.max_members ?? parsedSettings?.max_members,
        max_substitutes: teamData?.max_substitutes ?? parsedSettings?.max_substitutes,
        rules: teamData?.rules ?? parsedSettings?.rules,
      };

      const membersArray = membersResponse?.data?.members || membersResponse?.members || [];
      setMembers(Array.isArray(membersArray) ? membersArray : []);

      const ownerMember = Array.isArray(membersArray)
        ? membersArray.find((m) => ["owner", "creator"].includes(m.role))
        : null;
      if (ownerMember) {
        const fallbackName =
          ownerMember.username ||
          `${ownerMember.first_name || ""} ${ownerMember.last_name || ""}`.trim();
        if (!normalizedTeam.owner_name && fallbackName) {
          normalizedTeam.owner_name = fallbackName;
        }
        if (!normalizedTeam.owner_email && ownerMember.email) {
          normalizedTeam.owner_email = ownerMember.email;
        }
      }

      setTeam(normalizedTeam);

      // Roster summary (slot counts). The /roster endpoint returns members
      // only, so derive the counts from members + team settings (BUG-49).
      setRosterSummary(buildRosterSummary(normalizedTeam, membersArray));

      // The page can show now; join requests and tournament invitations
      // (owner / manager panels) fill in after it renders.
      setLoading(false);

      // Check user's membership status
      if (currentUser) {
        const activeMembership = Array.isArray(membersArray)
          ? membersArray.find((m) => m.user_id === currentUser.id && m.status === "active")
          : null;

        // Members who left and applicants who were rejected can apply again (TM-B04).
        const inactiveMembership = Array.isArray(membersArray)
          ? membersArray.find(
              (m) => m.user_id === currentUser.id && ["inactive", "rejected", "removed"].includes(m.status)
            )
          : null;

        setUserMembership(activeMembership);

        if (!activeMembership) {
          if (inactiveMembership) {
            setJoinRequestStatus("inactive");
          } else {
            try {
              const requests = await teamService.getJoinRequests(teamId);
              const userRequest = requests.find((req) => req.user_id === currentUser.id);
              if (userRequest) {
                setJoinRequestStatus(userRequest.status);
              }
            } catch (reqErr) {
              // No join request found
            }
          }
        }

        // Join requests are reviewed by the owner AND team managers/coaches
        // (TM-B25); tournament invitations stay with the owner.
        const isTeamOwner = Number(teamData?.created_by) === Number(currentUser.id);
        const isTeamManager = Boolean(
          activeMembership && TEAM_MEMBER_ADMIN_ROLES.includes(String(activeMembership.role || "").toLowerCase())
        );
        if (isTeamOwner || isTeamManager) {
          try {
            const requests = await teamService.getJoinRequests(teamId);
            const normalizedRequests = Array.isArray(requests)
              ? requests
              : requests?.data?.members || requests?.members || [];
            setJoinRequests(normalizedRequests);
          } catch (reqErr) {
            console.error("Error fetching join requests:", reqErr);
          }
        }
        if (isTeamOwner) {
          try {
            const invRes = await tournamentService.getMyTeamInvitations("pending");
            const invList = Array.isArray(invRes?.data) ? invRes.data : [];
            // show only invitations for this specific team
            setTournamentInvitations(
              invList.filter((inv) => Number(inv.team_id) === Number(teamId))
            );
          } catch (invErr) {
            console.error("Error fetching tournament invitations:", invErr);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching team data:", err);
      setError("Failed to load team details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [teamId, currentUser, isValidTeamId]);

  const fetchTeamStats = useCallback(async () => {
    if (!isValidTeamId) return;
    try {
      const statsResponse = await teamService.getTeamStats(teamId);
      const payload = statsResponse?.data || statsResponse;
      const statsData = payload?.data || payload || {};

      setTeamStatsSummary(statsData.summary || null);
      setStats(Array.isArray(statsData.topPerformers) ? statsData.topPerformers : []);
    } catch (err) {
      console.error("Error fetching team stats:", err);
      setTeamStatsSummary(null);
      setStats([]);
    }
  }, [teamId, isValidTeamId]);

  const fetchScheduleMatches = useCallback(async () => {
    if (!isValidTeamId) return;
    try {
      setLoadingMatches(true);
      const scheduleResponse = await teamService.getTeamSchedule(teamId);
      const matchesData =
        scheduleResponse?.message?.schedule ||
        scheduleResponse?.data?.schedule ||
        scheduleResponse?.schedule ||
        [];
      setScheduleMatches(Array.isArray(matchesData) ? matchesData : []);
    } catch (err) {
      console.error("Error fetching team schedule:", err);
      setScheduleMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, [teamId, isValidTeamId]);

  useEffect(() => {
    if (isValidTeamId) {
      fetchTeamData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, isValidTeamId]);

  return {
    team,
    setTeam,
    members,
    setMembers,
    rosterSummary,
    stats,
    teamStatsSummary,
    scheduleMatches,
    loadingMatches,
    loading,
    error,
    setError,
    joinRequests,
    setJoinRequests,
    tournamentInvitations,
    setTournamentInvitations,
    userMembership,
    setUserMembership,
    joinRequestStatus,
    setJoinRequestStatus,
    fetchTeamData,
    fetchTeamStats,
    fetchScheduleMatches,
  };
};

export default useTeamData;
